import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import Loader from '../../../components/Loader';
import Popup from '../../../components/Popup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiPlus, FiX, FiTrash2, FiSearch, FiRefreshCw, FiEdit2, FiClock,
    FiInbox, FiUsers, FiLayers, FiArrowUp, FiArrowDown, FiCheck,
} from 'react-icons/fi';
import { IMAGE_DOMAIN } from '../../../config/config';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CATEGORIAS, CATEGORIA_META } from './servicios/categorias';

const FILA_VACIA = () => ({ id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, texto: '' });

const ServiciosManager = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();


    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('todas');
    const [orden, setOrden] = useState('asc');

    // Formulario (alta y edición comparten modal)
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [servicioEditando, setServicioEditando] = useState(null);
    const [titulo, setTitulo] = useState('');
    const [categoria, setCategoria] = useState('desarrollo');
    const [horas, setHoras] = useState('');
    const [puntos, setPuntos] = useState([FILA_VACIA()]);
    const [guardando, setGuardando] = useState(false);

    const [servicioAEliminar, setServicioAEliminar] = useState(null);

    // Asignación masiva: desde el catálogo se elige de golpe a qué clientes
    // se les da un servicio, en vez de ir usuario por usuario.
    const [servicioAsignando, setServicioAsignando] = useState(null);
    const [seleccionUsuarios, setSeleccionUsuarios] = useState([]);
    const [busquedaUsuarios, setBusquedaUsuarios] = useState('');
    const [asignando, setAsignando] = useState(false);

    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const navigate = useNavigate();

    // La consulta ya se encarga de cargar el catálogo: aquí solo se protege
    // la ruta para quien no sea administrador.
    useEffect(() => {
        if (tokenInfo.role !== 'admin') navigate('/dashboard');
        // eslint-disable-next-line
    }, []);

    // Catálogo de servicios: misma caché que usa la ficha de usuario al
    // asignar servicios, así que abrir una y otra no repite la petición.
    const consultaServicios = useQuery({
        queryKey: claves.servicios(),
        queryFn: async () => {
            const resp = await axios.get(`${BASE_URL}/servicios`, cabecerasAuth());
            return resp.data.success ? (resp.data.servicios || []) : [];
        },
    });

    useEffect(() => {
        if (consultaServicios.isError) toast.error(t('gestorServicios.errorCargar'));
    }, [consultaServicios.isError, t]);

    // Usuarios para el modal de asignación. Comparte caché con el gestor de
    // usuarios, así que si ya se visitó no hay una segunda petición.
    const consultaUsuarios = useQuery({
        queryKey: claves.usuarios(),
        queryFn: async () => {
            const resp = await axios.get(`${BASE_URL}/todos-usuarios`, cabecerasAuth());
            return Array.isArray(resp.data.usuarios) ? resp.data.usuarios : [];
        },
        enabled: Boolean(servicioAsignando),
    });

    const usuarios = useMemo(() => consultaUsuarios.data || [], [consultaUsuarios.data]);

    const usuariosFiltrados = useMemo(() => {
        const texto = busquedaUsuarios.trim().toLowerCase();
        if (!texto) return usuarios;
        return usuarios.filter(u => (
            `${u.nombre || ''} ${u.apellidos || ''} ${u.usuario} ${u.email || ''}`.toLowerCase().includes(texto)
        ));
    }, [usuarios, busquedaUsuarios]);

    const abrirAsignacion = async (servicio) => {
        setServicioAsignando(servicio);
        setBusquedaUsuarios('');
        setSeleccionUsuarios([]);
        try {
            const resp = await axios.get(`${BASE_URL}/servicios/${servicio.id}/usuarios`, cabecerasAuth());
            if (resp.data.success) setSeleccionUsuarios(resp.data.usuarios || []);
        } catch (error) {
            toast.error(t('gestorServicios.errorCargarClientes'));
        }
    };

    const alternarUsuario = (usuario) => setSeleccionUsuarios(prev => (
        prev.includes(usuario) ? prev.filter(u => u !== usuario) : [...prev, usuario]
    ));

    // Marca o desmarca solo lo que hay a la vista: con un filtro activo, la
    // selección de los usuarios ocultos no se toca.
    const alternarVisibles = () => {
        const visibles = usuariosFiltrados.map(u => u.usuario);
        const todosMarcados = visibles.length > 0 && visibles.every(u => seleccionUsuarios.includes(u));
        setSeleccionUsuarios(prev => (todosMarcados
            ? prev.filter(u => !visibles.includes(u))
            : [...new Set([...prev, ...visibles])]));
    };

    const guardarAsignacion = async () => {
        if (!servicioAsignando) return;
        try {
            setAsignando(true);
            const resp = await axios.put(
                `${BASE_URL}/servicios/${servicioAsignando.id}/usuarios`,
                { usuarios: seleccionUsuarios },
                cabecerasAuth(),
            );
            if (resp.data.success) {
                toast.success(t('gestorServicios.clientesActualizados', { count: resp.data.usuarios.length }));
                setServicioAsignando(null);
                cargarServicios();
                // La ficha de cada usuario muestra sus servicios: queda obsoleta
                queryClient.invalidateQueries({ queryKey: ['usuario'] });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || t('gestorServicios.errorAsignarClientes'));
        } finally {
            setAsignando(false);
        }
    };

    const servicios = useMemo(() => consultaServicios.data || [], [consultaServicios.data]);
    const cargando = consultaServicios.isPending;
    const refrescando = consultaServicios.isFetching && !consultaServicios.isPending;

    const cargarServicios = () => queryClient.invalidateQueries({ queryKey: claves.servicios() });

    const serviciosFiltrados = useMemo(() => {
        let resultado = [...servicios];

        if (filtroCategoria !== 'todas') {
            resultado = resultado.filter(s => s.categoria === filtroCategoria);
        }

        if (busqueda.trim()) {
            const texto = busqueda.toLowerCase();
            resultado = resultado.filter(s =>
                s.titulo?.toLowerCase().includes(texto) ||
                (s.descripcion || []).some(p => p.toLowerCase().includes(texto))
            );
        }

        resultado.sort((a, b) => {
            const diff = a.titulo.localeCompare(b.titulo);
            return orden === 'asc' ? diff : -diff;
        });

        return resultado;
    }, [servicios, filtroCategoria, busqueda, orden]);

    const hayFiltrosActivos = filtroCategoria !== 'todas' || busqueda.trim() !== '';

    const limpiarFiltros = () => {
        setFiltroCategoria('todas');
        setBusqueda('');
    };

    const abrirNuevo = () => {
        setServicioEditando(null);
        setTitulo('');
        setCategoria('desarrollo');
        setHoras('');
        setPuntos([FILA_VACIA()]);
        setMostrarFormulario(true);
    };

    const abrirEdicion = (servicio) => {
        setServicioEditando(servicio);
        setTitulo(servicio.titulo);
        setCategoria(servicio.categoria);
        setHoras(servicio.horas === null || typeof servicio.horas === 'undefined' ? '' : String(servicio.horas));
        setPuntos(
            (servicio.descripcion || []).length > 0
                ? servicio.descripcion.map((texto, i) => ({ id: `p-${servicio.id}-${i}`, texto }))
                : [FILA_VACIA()]
        );
        setMostrarFormulario(true);
    };

    const cerrarFormulario = () => {
        setMostrarFormulario(false);
        setServicioEditando(null);
    };

    // ── Descripción: lista ampliable ──────────────────────────────────────
    const cambiarPunto = (id, texto) => {
        setPuntos(prev => prev.map(p => (p.id === id ? { ...p, texto } : p)));
    };

    const anadirPunto = () => setPuntos(prev => [...prev, FILA_VACIA()]);

    const quitarPunto = (id) => {
        setPuntos(prev => (prev.length === 1 ? [FILA_VACIA()] : prev.filter(p => p.id !== id)));
    };

    // Enter dentro de un punto crea el siguiente, como en una lista real
    const teclaEnPunto = (e, indice) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const nueva = FILA_VACIA();
        setPuntos(prev => [...prev.slice(0, indice + 1), nueva, ...prev.slice(indice + 1)]);
        // El foco se lleva al input recién creado en cuanto React lo pinta
        setTimeout(() => {
            const campo = document.querySelector(`[data-punto="${nueva.id}"]`);
            if (campo) campo.focus();
        }, 0);
    };

    const guardar = async (e) => {
        e.preventDefault();

        if (!titulo.trim()) {
            toast.error(t('gestorServicios.tituloObligatorio'));
            return;
        }
        if (horas !== '' && (Number.isNaN(Number(horas)) || Number(horas) < 0)) {
            toast.error(t('gestorServicios.horasPositivas'));
            return;
        }

        setGuardando(true);
        try {
            const datos = {
                titulo: titulo.trim(),
                categoria,
                horas: horas === '' ? null : Number(horas),
                descripcion: puntos.map(p => p.texto.trim()).filter(Boolean),
            };

            const resp = servicioEditando
                ? await axios.put(`${BASE_URL}/servicios/${servicioEditando.id}`, datos, { headers: { Authorization: token } })
                : await axios.post(`${BASE_URL}/servicios`, datos, { headers: { Authorization: token } });

            if (resp.data.success) {
                toast.success(resp.data.message);
                cerrarFormulario();
                cargarServicios();
            }
        } catch (error) {
            console.error('Error al guardar servicio:', error);
            toast.error(error.response?.data?.message || t('gestorServicios.errorGuardar'));
        } finally {
            setGuardando(false);
        }
    };

    const eliminarServicio = async () => {
        if (!servicioAEliminar) return;
        try {
            const resp = await axios.delete(`${BASE_URL}/servicios/${servicioAEliminar.id}`, {
                headers: { Authorization: token },
            });
            if (resp.data.success) {
                toast.success(t('gestorServicios.servicioEliminado'));
                cargarServicios();
            }
        } catch (error) {
            console.error('Error al eliminar servicio:', error);
            toast.error(t('gestorServicios.errorEliminar'));
        } finally {
            setServicioAEliminar(null);
        }
    };

    const resumen = useMemo(() => ({
        total: servicios.length,
        asignados: servicios.reduce((acc, s) => acc + (s.usuarios_asignados || 0), 0),
        horas: servicios.reduce((acc, s) => acc + (s.horas || 0), 0),
    }), [servicios]);

    if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

    return (
        <main className="site-width">
            <ToastContainer position="bottom-right" autoClose={3000} />

            <div className="gn-page">
                <div className="gn-header">
                    <div className="pagina-cabecera">
                        <h2>{t('gestorServicios.titulo')}</h2>
                        <p>{t('gestorServicios.subtitulo')}</p>
                    </div>
                    <div className="gn-header-acciones">
                        <button
                            className="gn-boton-icono"
                            onClick={cargarServicios}
                            disabled={refrescando}
                            title={t('gestorServicios.actualizar')}
                        >
                            <FiRefreshCw className={refrescando ? 'gn-girando' : ''} />
                        </button>
                        <button className="boton-primario gn-boton-nueva" onClick={abrirNuevo}>
                            <FiPlus /> {t('gestorServicios.nuevoServicio')}
                        </button>
                    </div>
                </div>

                <div className="gn-stats">
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiLayers /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.total}</p>
                            <p className="gn-stat-label">{t('gestorServicios.serviciosEnCatalogo')}</p>
                            <p className="gn-stat-sub">{t('gestorServicios.disponiblesParaAsignar')}</p>
                        </div>
                    </div>
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiUsers /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.asignados}</p>
                            <p className="gn-stat-label">{t('gestorServicios.asignacionesActivas')}</p>
                            <p className="gn-stat-sub">{t('gestorServicios.serviciosVinculados')}</p>
                        </div>
                    </div>
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiClock /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.horas}</p>
                            <p className="gn-stat-label">{t('gestorServicios.horasCatalogo')}</p>
                            <p className="gn-stat-sub">{t('gestorServicios.sumaHoras')}</p>
                        </div>
                    </div>
                </div>

                <div className="gn-toolbar">
                    <div className="gn-campo-icono gn-buscador">
                        <FiSearch className="gn-icono-input" />
                        <input
                            type="text"
                            placeholder={t('gestorServicios.buscarPorTituloDesc')}
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            className="campo-busqueda gn-input-con-icono"
                        />
                    </div>
                    <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)} className="selector">
                        <option value="todas">{t('gestorServicios.todasCategorias')}</option>
                        {CATEGORIAS.map(valor => (
                            <option key={valor} value={valor}>{CATEGORIA_META[valor].label}</option>
                        ))}
                    </select>
                    <button
                        className="gn-boton-icono"
                        onClick={() => setOrden(o => (o === 'asc' ? 'desc' : 'asc'))}
                        title={orden === 'asc' ? 'A-Z' : 'Z-A'}
                    >
                        {orden === 'asc' ? <FiArrowDown /> : <FiArrowUp />}
                    </button>
                </div>

                <div className="gn-lista">
                    <div className="gn-lista-cabecera">
                        <p className="info-resultados">
                            {t('gestorServicios.mostrandoDeServicios', { visibles: serviciosFiltrados.length, total: servicios.length })}
                        </p>
                    </div>

                    {serviciosFiltrados.length > 0 ? (
                        <div className="srv-tabla">
                            <div className="srv-tabla-cabecera">
                                <span>{t('gestorServicios.servicio')}</span>
                                <span>{t('gestorServicios.categoria')}</span>
                                <span>{t('gestorServicios.horas')}</span>
                                <span>{t('gestorServicios.clientes')}</span>
                                <span className="srv-col-acciones">{t('gestorServicios.acciones')}</span>
                            </div>

                            {serviciosFiltrados.map((servicio) => {
                                const meta = CATEGORIA_META[servicio.categoria] || CATEGORIA_META.desarrollo;
                                return (
                                    <div key={servicio.id} className="srv-fila">
                                        <div className="srv-celda-servicio">
                                            <h4>{servicio.titulo}</h4>
                                            {/* Una sola línea: la lista completa disparaba la altura de
                                                la fila y el detalle ya se ve al editar el servicio */}
                                            {servicio.descripcion?.length > 0 ? (
                                                <p className="srv-resumen" title={servicio.descripcion.join(' · ')}>
                                                    {servicio.descripcion.join(' · ')}
                                                </p>
                                            ) : (
                                                <p className="srv-sin-descripcion">{t('gestorServicios.sinDescripcion')}</p>
                                            )}
                                        </div>

                                        <div className="srv-celda">
                                            <span className={`srv-categoria srv-categoria--${servicio.categoria}`}>
                                                {meta.label}
                                            </span>
                                        </div>

                                        <div className="srv-celda">
                                            {servicio.horas !== null && typeof servicio.horas !== 'undefined' ? (
                                                <span className="srv-horas"><FiClock /> {servicio.horas} h</span>
                                            ) : (
                                                <span className="srv-vacio-celda">—</span>
                                            )}
                                        </div>

                                        <div className="srv-celda">
                                            <button
                                                type="button"
                                                className="srv-clientes"
                                                onClick={() => abrirAsignacion(servicio)}
                                                title={t('gestorServicios.asignarClientes')}
                                            >
                                                <FiUsers /> {servicio.usuarios_asignados || 0}
                                            </button>
                                        </div>

                                        <div className="srv-celda srv-col-acciones">
                                            <button
                                                type="button"
                                                className="gn-boton-icono gn-boton-icono--sm"
                                                onClick={() => abrirAsignacion(servicio)}
                                                title={t('gestorServicios.asignarClientes')}
                                            >
                                                <FiUsers />
                                            </button>
                                            <button
                                                type="button"
                                                className="gn-boton-icono gn-boton-icono--sm"
                                                onClick={() => abrirEdicion(servicio)}
                                                title={t('gestorServicios.editarServicio')}
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                type="button"
                                                className="gn-boton-icono gn-boton-icono--sm srv-boton-eliminar"
                                                onClick={() => setServicioAEliminar(servicio)}
                                                title={t('gestorServicios.eliminarServicio')}
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gn-vacio">
                            <FiInbox />
                            <p>{servicios.length === 0 ? t('gestorServicios.sinServiciosCatalogo') : t('gestorServicios.sinServiciosEncontrados')}</p>
                            {hayFiltrosActivos ? (
                                <button className="gn-boton-cancelar" onClick={limpiarFiltros}>{t('gestorServicios.limpiarFiltros')}</button>
                            ) : (
                                <button className="boton-primario gn-boton-nueva" onClick={abrirNuevo}>
                                    <FiPlus /> {t('gestorServicios.crearElPrimero')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {mostrarFormulario && (
                <div className="gn-modal-overlay" onClick={cerrarFormulario}>
                    <div className="gn-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gn-modal-header">
                            <h3>{servicioEditando ? <><FiEdit2 /> {t('gestorServicios.editarServicio')}</> : <><FiPlus /> {t('gestorServicios.nuevoServicio')}</>}</h3>
                            <button type="button" className="gn-boton-cerrar" onClick={cerrarFormulario}>
                                <FiX />
                            </button>
                        </div>

                        <form onSubmit={guardar} className="gn-modal-form">
                            <div className="campo-formulario">
                                <label>{t('gestorServicios.titulo2')}</label>
                                <input
                                    type="text"
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    placeholder={t('gestorServicios.placeholderTitulo')}
                                    maxLength={150}
                                    required
                                />
                            </div>

                            <div className="campo-formulario">
                                <label>{t('gestorServicios.categoriaObligatoria')}</label>
                                <div className="gn-chips">
                                    {CATEGORIAS.map((valor) => {
                                        const { label, icon: Icono } = CATEGORIA_META[valor];
                                        return (
                                            <button
                                                type="button"
                                                key={valor}
                                                className={`gn-chip ${categoria === valor ? 'activo' : ''}`}
                                                onClick={() => setCategoria(valor)}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="campo-formulario">
                                <label>{t('gestorServicios.horasOpcional')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={horas}
                                    onChange={(e) => setHoras(e.target.value)}
                                    placeholder={t('gestorServicios.placeholderHoras')}
                                />
                                <small>{t('gestorServicios.hintHoras')}</small>
                            </div>

                            <div className="campo-formulario">
                                <label>{t('gestorServicios.descripcionOpcional')}</label>
                                <div className="srv-editor-lista">
                                    {puntos.map((punto, indice) => (
                                        <div key={punto.id} className="srv-editor-fila">
                                            <span className="srv-editor-bullet" aria-hidden="true" />
                                            <input
                                                type="text"
                                                data-punto={punto.id}
                                                value={punto.texto}
                                                onChange={(e) => cambiarPunto(punto.id, e.target.value)}
                                                onKeyDown={(e) => teclaEnPunto(e, indice)}
                                                placeholder={t('gestorServicios.placeholderPunto')}
                                            />
                                            <button
                                                type="button"
                                                className="srv-editor-quitar"
                                                onClick={() => quitarPunto(punto.id)}
                                                title={t('gestorServicios.quitarPunto')}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="srv-editor-anadir" onClick={anadirPunto}>
                                    <FiPlus /> {t('gestorServicios.anadirPunto')}
                                </button>
                                <small>{t('gestorServicios.hintEnter')}</small>
                            </div>

                            <div className="botones-formulario">
                                <button type="submit" className="boton-primario" disabled={guardando}>
                                    <FiCheck /> {guardando ? t('gestorServicios.guardando') : (servicioEditando ? t('gestorServicios.guardarCambios') : t('gestorServicios.crearServicio'))}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {servicioAsignando && (
                <div className="gn-modal-overlay" onClick={() => setServicioAsignando(null)}>
                    <div className="gn-modal srv-modal-clientes" onClick={(e) => e.stopPropagation()}>
                        <div className="gn-modal-header">
                            <h3><FiUsers /> {t('gestorServicios.asignarClientes')}</h3>
                            <button type="button" className="gn-boton-cerrar" onClick={() => setServicioAsignando(null)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="srv-modal-cuerpo">
                        <p className="srv-modal-sub">
                            <Trans i18nKey="gestorServicios.asignarClientesTexto" values={{ titulo: servicioAsignando.titulo }}>
                                <b>{servicioAsignando.titulo}</b>
                            </Trans>
                        </p>

                        <div className="srv-modal-barra">
                            <div className="gn-campo-icono">
                                <FiSearch className="gn-icono-input" />
                                <input
                                    type="text"
                                    value={busquedaUsuarios}
                                    onChange={(e) => setBusquedaUsuarios(e.target.value)}
                                    placeholder={t('gestorServicios.buscarCliente')}
                                    className="campo-busqueda gn-input-con-icono"
                                />
                            </div>
                            <button type="button" className="gn-boton-cancelar" onClick={alternarVisibles} disabled={usuariosFiltrados.length === 0}>
                                {t('gestorServicios.alternarVisibles')}
                            </button>
                        </div>

                        <div className="srv-modal-lista">
                            {consultaUsuarios.isPending ? (
                                <p className="srv-modal-vacio">{t('gestorServicios.cargandoClientes')}</p>
                            ) : usuariosFiltrados.length === 0 ? (
                                <p className="srv-modal-vacio">{t('gestorServicios.sinClientes')}</p>
                            ) : usuariosFiltrados.map((usuario) => {
                                const marcado = seleccionUsuarios.includes(usuario.usuario);
                                return (
                                    <label key={usuario.usuario} className={`srv-modal-usuario ${marcado ? 'marcado' : ''}`}>
                                        <input type="checkbox" checked={marcado} onChange={() => alternarUsuario(usuario.usuario)} />
                                        <img
                                            src={usuario.fotoPerfil && usuario.fotoPerfil !== 'none'
                                                ? (usuario.fotoPerfil.startsWith('http') ? usuario.fotoPerfil : `${IMAGE_DOMAIN}${usuario.fotoPerfil}`)
                                                : `${IMAGE_DOMAIN}/images/default-user.png`}
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span className="srv-modal-datos">
                                            <strong>{[usuario.nombre, usuario.apellidos].filter(Boolean).join(' ') || usuario.usuario}</strong>
                                            <small>@{usuario.usuario} · {usuario.email}</small>
                                        </span>
                                        {usuario.role !== 'user' && (
                                            <span className="srv-modal-rol">{t(`gestorUsuarios.roles.${usuario.role}`)}</span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>

                        </div>

                        <div className="srv-modal-pie">
                            <span>{t('gestorServicios.seleccionados', { count: seleccionUsuarios.length })}</span>
                            <div className="botones-formulario">
                                <button type="button" className="gn-boton-cancelar" onClick={() => setServicioAsignando(null)}>
                                    {t('comun.cancelar')}
                                </button>
                                <button type="button" className="boton-primario" onClick={guardarAsignacion} disabled={asignando}>
                                    <FiCheck /> {asignando ? t('gestorServicios.guardando') : t('gestorServicios.guardarCambios')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Popup
                open={Boolean(servicioAEliminar)}
                title={t('gestorServicios.eliminarServicio')}
                onClose={() => setServicioAEliminar(null)}
                showConfirm={false}
                footerActions={(
                    <button className="boton-aceptar-popup" onClick={eliminarServicio}>{t('gestorServicios.eliminar')}</button>
                )}
            >
                <p><Trans i18nKey="gestorServicios.vasAEliminar" values={{ titulo: servicioAEliminar?.titulo }}><b>{servicioAEliminar?.titulo}</b></Trans></p>
                {servicioAEliminar?.usuarios_asignados > 0 && (
                    <p>{t('gestorServicios.seDesasignara', { count: servicioAEliminar.usuarios_asignados })}</p>
                )}
                <p>{t('gestorServicios.accionNoDeshacer')}</p>
            </Popup>
        </main>
    );
};

export default ServiciosManager;
